import { z } from 'zod';
import { StatusTypeSchema } from '../enums/status.js';
import { authDetailsSchema, authSchema } from './auth.js';
import { HintItemSchema } from './hint-items.js';

export const greetingInitialMessageConfigSchema = z.object({
  type: z.literal('greeting-starter-questions'),
  message: z.string().nullable(),
  starterItems: z.array(HintItemSchema).default([]),
});

export const hcpSelectionInitialMessageConfigSchema = z.object({
  type: z.literal('hcp-selection'),
  message: z.string().nullable(),
});

export const markdownInitialMessageConfigSchema = z.object({
  type: z.literal('markdown'),
  message: z.string().nullable(),
});

export const frmAccountSearchInitialMessageConfigSchema = z.object({
  type: z.literal('frm-account-search'),
  message: z.string().nullable(),
});

export const initialMessageConfigSchema = z.discriminatedUnion('type', [
  greetingInitialMessageConfigSchema,
  hcpSelectionInitialMessageConfigSchema,
  markdownInitialMessageConfigSchema,
  frmAccountSearchInitialMessageConfigSchema,
]);
export type InitialMessageConfig = z.infer<typeof initialMessageConfigSchema>;

const defaultInitialMessageConfig: InitialMessageConfig = {
  type: 'greeting-starter-questions',
  message: null,
  starterItems: [],
};

export const subjectAutocompleteDataSchema = z.object({
  endpoint: z.string(),
  queryParam: z.string().optional(),
  auth: authSchema,
  required: z.boolean(),
});
export type SubjectAutocompleteData = z.infer<typeof subjectAutocompleteDataSchema>;

export const chatContextLayoutSchema = z.enum(['full_page', 'side_chat']);
export type ChatContextLayout = z.infer<typeof chatContextLayoutSchema>;

// Schema with detailed auth data
export const subjectAutocompleteDetailsSchema = z.object({
  endpoint: z.string(),
  queryParam: z.string().optional(),
  auth: authDetailsSchema,
  required: z.boolean(),
});
export type SubjectAutocompleteDetails = z.infer<typeof subjectAutocompleteDetailsSchema>;

export const chatContextTableItemSchema = z.object({
  id: z.coerce.number(),
  identifier: z.string(),
  name: z.string(),
  status: StatusTypeSchema,
  primaryAgent: z.object({
    id: z.coerce.number(),
    name: z.string(),
  }),
  applications: z.array(
    z.object({
      id: z.coerce.number(),
      name: z.string(),
    })
  ),
  metadata: z.object({
    applications: z.number(),
  }),
});
export type ChatContextTableItem = z.infer<typeof chatContextTableItemSchema>;

export const chatContextDataSchema = z.object({
  chatNameTemplate: z.string(),
  chatNameGenerationPromptId: z.number().nullable(),
  createdOn: z.coerce.date(),
  id: z.coerce.number(),
  identifier: z.string(),
  name: z.string(),
  status: StatusTypeSchema,
  primaryAgent: z.object({
    id: z.coerce.number(),
    identifier: z.string(),
    name: z.string(),
  }),
  copilotsToChatContexts: z.array(z.object({ copilotId: z.number(), copilotIdentifier: z.string().optional() })).optional(),
  initialMessageConfig: initialMessageConfigSchema.default(defaultInitialMessageConfig),
  additionalAgentContext: z.record(z.string(), z.unknown()),
  subjectAutocomplete: subjectAutocompleteDataSchema.nullable(),
  showLandingCardsOnNewChat: z.boolean().prefault(false),
});
export type ChatContextData = z.infer<typeof chatContextDataSchema>;

export const chatContextDetailsSchema = z.object({
  chatNameTemplate: z.string(),
  chatNameGenerationPrompt: z.object({ id: z.number(), identifier: z.string(), name: z.string() }).nullable(),
  createdOn: z.coerce.date(),
  id: z.coerce.number(),
  identifier: z.string(),
  name: z.string(),
  status: StatusTypeSchema,
  primaryAgent: z.object({
    id: z.coerce.number(),
    identifier: z.string(),
    name: z.string(),
  }),
  copilotsToChatContexts: z.array(z.object({ copilotId: z.number(), copilotIdentifier: z.string().optional() })).optional(),
  initialMessageConfig: initialMessageConfigSchema.default(defaultInitialMessageConfig),
  additionalAgentContext: z.record(z.string(), z.unknown()),
  subjectAutocomplete: subjectAutocompleteDetailsSchema.nullable(),
  showLandingCardsOnNewChat: z.boolean().prefault(false),
});
export type ChatContextDetails = z.infer<typeof chatContextDetailsSchema>;
